'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  TankJgResponse,
  TankJgStockDto,
  TankJgStockHistoryDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function TankJg() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();

  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [stockData, setStockData] = useState<TankJgStockDto[]>([]);
  const [historyData, setHistoryData] = useState<TankJgStockHistoryDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // 탱크재고 데이터 가져오기 함수
  const fetchTankJgData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    if (!filters.dateRange?.start || !filters.dateRange?.end) {
      return;
    }

    setIsLoading(true);
    try {
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 필터 스토어의 buildApiParams 메서드 사용
      const params = filters.buildApiParams('tankjg', storeCodeList);

      const response = await apiClient.get(`stock/tankjg`, {
        searchParams: params,
      });
      const data = (await response.json()) as {
        result: TankJgResponse;
        status: number;
        message?: string;
      };

      if (data.result) {
        setStockData(data.result.stock || []);
        setHistoryData(data.result.history || []);
      } else {
        toast({
          title: '오류',
          description: data.message || '데이터 조회에 실패했습니다.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Tank inventory data fetch error:', error);
      toast({
        title: '오류',
        description: '데이터 조회 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 검색 핸들러
  const handleSearch = () => {
    fetchTankJgData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchTankJgData();
  }, []);

  return (
    <div>
      {/* 탱크 재고 현황 그리드 */}
      <ExpandableCard
        title='탱크 재고 현황'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('tankjg')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        }
        onExpandChange={setIsExpanded}
      >
        <div
          className={`border rounded-lg ${isExpanded ? 'h-full' : 'h-[calc(50vh-140px)]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
                grouping: { enabled: true },
              }}
              rows={stockData}
            >
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tkCode'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkType'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tkOtype'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tkTotal'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkBefore'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkIn'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkOut'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkCurr'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'percent'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'tkTlg'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amtError'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '주유소' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'tkCode'}
                header={{ text: '탱크' }}
                width={60}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tkTotal'}
                header={{ text: '총용량' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tkBefore'}
                header={{ text: '전일재고' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tkIn'}
                header={{ text: '입고량' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tkOut'}
                header={{ text: '출고량' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'tkCurr'}
                header={{ text: '현재고' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'percent'}
                header={{ text: '재고율(%)' }}
                width={100}
                numberFormat='#,##0.0'
              />
              <RGDataColumn
                fieldName={'tkTlg'}
                header={{ text: 'TLG재고' }}
                width={80}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'amtError'}
                header={{ text: '오차량' }}
                width={80}
                numberFormat='#,##0'
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>

      <div>
        <ExpandableCard title='탱크 재고 내역' onExpandChange={() => { }}>
          <div className={`border rounded-lg h-[calc(50vh-140px)]`}>
            <div className='w-full h-full'>
              <RealGridReact
                gridProps={{
                  display: { fitStyle: RealGrid.GridFitStyle.FILL },
                  checkBar: { visible: false },
                  stateBar: { visible: false },
                  edit: { readOnly: true, editable: false },
                  footer: { visible: false },
                }}
                rows={historyData}
              >
                <RGDataField
                  fieldName={'compName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'storeName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'closedt'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'closecnt'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'saleNo'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'cardNo2'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'typeName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'qty'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'cost'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'amount'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'pumpNo'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'tank'}
                  dataType={RealGrid.ValueType.NUMBER}
                />
                <RGDataField
                  fieldName={'oilType'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'oilName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'dealCode'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'dealName'}
                  dataType={RealGrid.ValueType.TEXT}
                />
                <RGDataField
                  fieldName={'bigo'}
                  dataType={RealGrid.ValueType.TEXT}
                />

                <RGDataColumn
                  fieldName={'storeName'}
                  header={{ text: '주유소' }}
                  width={120}
                />
                <RGDataColumn
                  fieldName={'closedt'}
                  header={{ text: '마감일' }}
                  width={80}
                />
                <RGDataColumn
                  fieldName={'saleNo'}
                  header={{ text: '판매번호' }}
                  width={80}
                />
                <RGDataColumn
                  fieldName={'cardNo2'}
                  header={{ text: '카드번호' }}
                  width={120}
                />
                <RGDataColumn
                  fieldName={'typeName'}
                  header={{ text: '판매구분' }}
                  width={80}
                />
                <RGDataColumn
                  fieldName={'qty'}
                  header={{ text: '수량' }}
                  width={80}
                  numberFormat='#,##0.00'
                />
                <RGDataColumn
                  fieldName={'cost'}
                  header={{ text: '단가' }}
                  width={80}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'amount'}
                  header={{ text: '금액' }}
                  width={100}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'pumpNo'}
                  header={{ text: '펌프' }}
                  width={60}
                />
                <RGDataColumn
                  fieldName={'tank'}
                  header={{ text: '탱크' }}
                  width={60}
                  numberFormat='#,##0'
                />
                <RGDataColumn
                  fieldName={'oilName'}
                  header={{ text: '유종' }}
                  width={80}
                />
                <RGDataColumn
                  fieldName={'dealName'}
                  header={{ text: '거래처' }}
                  width={120}
                />
                <RGDataColumn
                  fieldName={'bigo'}
                  header={{ text: '비고' }}
                  width={150}
                />
              </RealGridReact>
            </div>
          </div>
        </ExpandableCard>
      </div>
    </div>
  );
}
